/**
 * webauthn.ts — Real WebAuthn ceremony helpers.
 *
 * Replaces the prior stub in WebAuthnEnrollFlow that passed the server's
 * options blob straight to navigator.credentials.create(). The WebAuthn spec
 * requires base64url <-> ArrayBuffer conversion on:
 *   - challenge          (server → client → server)
 *   - user.id            (server → client)
 *   - excludeCredentials[].id (server → client)
 *   - rawId              (client → server)
 *   - response.clientDataJSON (client → server)
 *   - response.attestationObject  (registration; client → server)
 *   - response.authenticatorData / signature / userHandle (assertion; client → server)
 *
 * This module supplies the bidirectional codec + a typed wrapper for both the
 * registration and assertion ceremonies. The signature dialogs and the 2FA
 * enrollment screen call into here instead of doing it inline.
 *
 * URS rows: AUTH-003..006, SEC-021.
 */

// ----- base64url codec -----

/** Decode a base64url string (no padding, "-" + "_" alphabet) to an ArrayBuffer. */
export function b64urlToBuffer(b64url: string): ArrayBuffer {
  const padding = '='.repeat((4 - (b64url.length % 4)) % 4);
  const b64 = (b64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out.buffer;
}

/** Encode an ArrayBuffer (or view) to a base64url string (no padding). */
export function bufferToB64url(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let bin = '';
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i] ?? 0);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ----- ceremony helpers -----

/** Server-shaped registration options — IDs come as base64url strings. */
export interface ServerPublicKeyCredentialCreationOptions {
  rp: { id: string; name: string };
  user: { id: string; name: string; displayName: string };
  challenge: string; // base64url
  pubKeyCredParams: { type: 'public-key'; alg: number }[];
  timeout?: number;
  attestation?: AttestationConveyancePreference;
  excludeCredentials?: { id: string; type: 'public-key'; transports?: AuthenticatorTransport[] }[];
  authenticatorSelection?: AuthenticatorSelectionCriteria;
}

/** Client-shaped registration options — IDs are ArrayBuffers per spec. */
export function decodeRegistrationOptions(
  opts: ServerPublicKeyCredentialCreationOptions,
): PublicKeyCredentialCreationOptions {
  return {
    rp: opts.rp,
    user: {
      id: b64urlToBuffer(opts.user.id),
      name: opts.user.name,
      displayName: opts.user.displayName,
    },
    challenge: b64urlToBuffer(opts.challenge),
    pubKeyCredParams: opts.pubKeyCredParams,
    timeout: opts.timeout,
    attestation: opts.attestation,
    excludeCredentials: opts.excludeCredentials?.map((c) => ({
      id: b64urlToBuffer(c.id),
      type: c.type,
      transports: c.transports,
    })),
    authenticatorSelection: opts.authenticatorSelection,
  };
}

/** Server-shaped assertion options. */
export interface ServerPublicKeyCredentialRequestOptions {
  challenge: string; // base64url
  timeout?: number;
  rpId?: string;
  allowCredentials?: { id: string; type: 'public-key'; transports?: AuthenticatorTransport[] }[];
  userVerification?: UserVerificationRequirement;
}

export function decodeAssertionOptions(
  opts: ServerPublicKeyCredentialRequestOptions,
): PublicKeyCredentialRequestOptions {
  return {
    challenge: b64urlToBuffer(opts.challenge),
    timeout: opts.timeout,
    rpId: opts.rpId,
    allowCredentials: opts.allowCredentials?.map((c) => ({
      id: b64urlToBuffer(c.id),
      type: c.type,
      transports: c.transports,
    })),
    userVerification: opts.userVerification,
  };
}

/** Wire DTO sent BACK to the server after a registration ceremony. */
export interface ClientRegistrationResponse {
  id: string;
  rawId: string;          // base64url
  type: 'public-key';
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
  response: {
    clientDataJSON: string;       // base64url
    attestationObject: string;    // base64url
    transports?: AuthenticatorTransport[];
  };
}

export function encodeRegistrationResponse(
  cred: PublicKeyCredential,
): ClientRegistrationResponse {
  const att = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: bufferToB64url(cred.rawId),
    type: 'public-key',
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToB64url(att.clientDataJSON),
      attestationObject: bufferToB64url(att.attestationObject),
      transports: typeof att.getTransports === 'function'
        ? (att.getTransports() as AuthenticatorTransport[])
        : undefined,
    },
  };
}

/** Wire DTO sent BACK to the server after an assertion ceremony. */
export interface ClientAssertionResponse {
  id: string;
  rawId: string;
  type: 'public-key';
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    userHandle: string | null;
  };
}

export function encodeAssertionResponse(
  cred: PublicKeyCredential,
): ClientAssertionResponse {
  const ass = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bufferToB64url(cred.rawId),
    type: 'public-key',
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToB64url(ass.clientDataJSON),
      authenticatorData: bufferToB64url(ass.authenticatorData),
      signature: bufferToB64url(ass.signature),
      userHandle: ass.userHandle ? bufferToB64url(ass.userHandle) : null,
    },
  };
}

/** True if this browser exposes WebAuthn at all. */
export function isWebAuthnSupported(): boolean {
  return typeof window !== 'undefined'
    && typeof window.PublicKeyCredential !== 'undefined';
}

/** True if a platform authenticator (Touch ID / Windows Hello) is available. */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
//  performAssertionCeremony — C1 wire-up (2026-05-28).
//
//  Drives the full assertion ceremony end-to-end from a signing dialog:
//    1. POST /api/v1/auth/2fa/webauthn/assertion/options → fresh challenge
//       + allowCredentials, persisted server-side with 5-min TTL.
//    2. navigator.credentials.get(...) with the decoded options → user
//       prompted for Touch ID / Windows Hello / security key.
//    3. encodeAssertionResponse() → ClientAssertionResponse the server can
//       feed straight into @simplewebauthn/server's verifyAuthenticationResponse.
//
//  Throws WebAuthnEnrollmentRequiredError if the user has no registered
//  credentials yet — caller routes them to /2fa/enrollment.
// ---------------------------------------------------------------------------

export class WebAuthnEnrollmentRequiredError extends Error {
  constructor() {
    super('No WebAuthn credentials registered for this user.');
    this.name = 'WebAuthnEnrollmentRequiredError';
  }
}

export interface AssertionOptionsResponse {
  publicKey: ServerPublicKeyCredentialRequestOptions;
  registeredCount: number;
  enrollmentRequired?: boolean;
}

/**
 * Drive the assertion ceremony end-to-end.
 * Caller provides a `fetchOptions` closure (typically wrapping `apiFetch`)
 * so this module stays decoupled from the SPA's HTTP client.
 */
export async function performAssertionCeremony(
  fetchOptions: () => Promise<AssertionOptionsResponse>,
): Promise<ClientAssertionResponse> {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn not supported by this browser.');
  }
  const opts = await fetchOptions();
  if (opts.enrollmentRequired || opts.registeredCount === 0) {
    throw new WebAuthnEnrollmentRequiredError();
  }
  const requestOptions = decodeAssertionOptions(opts.publicKey);
  const cred = (await navigator.credentials.get({ publicKey: requestOptions })) as PublicKeyCredential | null;
  if (!cred) throw new Error('Authenticator returned no credential.');
  return encodeAssertionResponse(cred);
}
